import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SequenceItem } from '../../../components/Sequence/SequenceItem.tsx';
import { BiddingCardColorView, BiddingCardValueView, BiddingDeclarationView, BiddingView } from '../../../graphql/types.gen.ts';

const SEQUENCE_ITEMS: BiddingView[] = [];
Object.keys(BiddingCardValueView).forEach((value) => {
    Object.keys(BiddingCardColorView).forEach((color) => {
        SEQUENCE_ITEMS.push({
            bidCard: {
                __typename: 'BiddingCardView',
                value: BiddingCardValueView[value],
                color: BiddingCardColorView[color],
            },
            declaration: BiddingDeclarationView.Bid,
            shouldAlert: false,
        });
    });
    SEQUENCE_ITEMS.push({
        bidCard: undefined,
        declaration: BiddingDeclarationView.Pass,
        shouldAlert: false,
    });
});

SEQUENCE_ITEMS.push({
    bidCard: undefined,
    declaration: BiddingDeclarationView.Double,
    shouldAlert: false,
});
SEQUENCE_ITEMS.push({
    bidCard: undefined,
    declaration: BiddingDeclarationView.Redouble,
    shouldAlert: false,
});

export const FlyingBids = ({ direction = 'right' }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [scrollOffset, setScrollOffset] = useState(0);
    const containerRef = useRef(null);
    const initialScrollRef = useRef(0);
    const lastScrollUpdate = useRef(0);

    // Detect when component is visible in viewport
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const isNowVisible = entry.isIntersecting;
                    setIsVisible(isNowVisible);

                    // When the element becomes visible, store the current scroll position
                    if (isNowVisible) {
                        initialScrollRef.current = window.scrollY;
                    }
                });
            },
            { threshold: 0.1 } // Trigger when at least 10% is visible
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
        };
    }, []);

    // Throttled scroll handler
    const handleScroll = useCallback(() => {
        if (!isVisible) return;

        const now = Date.now();
        // Throttle to approximately 60fps (16.6ms)
        if (now - lastScrollUpdate.current >= 16) {
            // Calculate relative scroll from when element became visible
            const relativeScroll = window.scrollY - initialScrollRef.current;
            setScrollOffset(relativeScroll);
            lastScrollUpdate.current = now;
        }
    }, [isVisible]);

    // Track scroll position when element is visible
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    const SHUFFLED_SEQUENCE_ITEMS = useMemo(() => {
        return SEQUENCE_ITEMS.map((value) => ({ value, sort: Math.random() }))
            .sort((a, b) => a.sort - b.sort)
            .map(({ value }) => value);
    }, []);

    // Calculate movement based on scroll position
    const movement = isVisible ? (direction === 'left' ? scrollOffset * 0.3 : -scrollOffset * 0.3) : 0;

    return (
        <div
            ref={containerRef}
            className={`home__flying-bids home__flying-bids--${direction}`}
            style={{
                transform: `translateX(${movement}px)`,
            }}
        >
            {!!SHUFFLED_SEQUENCE_ITEMS.length &&
                [...SHUFFLED_SEQUENCE_ITEMS, ...SHUFFLED_SEQUENCE_ITEMS, ...SHUFFLED_SEQUENCE_ITEMS].map((item, index) => {
                    return <SequenceItem key={index} item={item} classes={'home__flying-bid'} />;
                })}
        </div>
    );
};
